import { EndUsersDetailTab } from '@/features/end-users';
import { BenefitsTab } from '@/features/benefits';
import { FsDetailTab, FsTab } from '@/features/frekuent-spots';
import { SettingsTab, ToolsTab } from '@/shared/constants';
import { AlertsTab } from '@/features/alerts';
import { BenefitsPromotionTabs } from '@/features/benefits/promotions/constants';
import { BenefitsPacksTabs } from '@/features/benefits/packs/constants';
import { TagsNfcTab } from '@/features/end-users/tags-nfc';
import { QrsTab } from '@/features/end-users/qrs';
import { SupportTab } from '@/features/support';
import { TransactionType } from '@/legacy/types';
import { ReconciliationTab } from '@/features/reconciliations/constants';
import { MarketplaceTab } from '@/features/marketplace/constants';

export const paths = {
	auth: {
		login: () => '/login',
		beta: (token: string) => `/beta/${token}`,
		forgotPassword: () => '/forgot-password',
		signUp: () => '/sign-up',
	},

	home: () => '/app/home',

	frekuentSpots: {
		base: () => '/app/frekuent-spots',
		list: () => '/app/frekuent-spots/points-of-sale',
		details: (posId: string) => `/app/frekuent-spots/points-of-sale/${posId}`,
		detailsTabs: (posId: string, tab: FsDetailTab) =>
			`/app/frekuent-spots/points-of-sale/${posId}/${tab}`,
		listTabs: (tab: FsTab) => `/app/frekuent-spots/points-of-sale/${tab}`,
		activityDetails: (posId: string, transactionId: string, type: string) =>
			`/app/frekuent-spots/points-of-sale/${posId}/activity/${transactionId}?type=${type}`,
		create: () => '/app/frekuent-spots/points-of-sale/create',
		products: () => '/app/frekuent-spots/products',
		posPersonalization: {
			base: () => '/app/frekuent-spots/pos-personalization',
			drafts: () => '/app/frekuent-spots/pos-personalization/drafts',
			create: () => '/app/frekuent-spots/pos-personalization/create',
			edit: (id: string | number) => `/app/frekuent-spots/pos-personalization/edit/${id}`,
			listTabs: (tab: FsTab) => `/app/frekuent-spots/pos-personalization/${tab}`,
		},
		stock: () => '/app/frekuent-spots/products/stock',
		productDetails: () => '/app/frekuent-spots/products/stock/products-details',
	},

	users: {
		base: () => '/app/users',
		users: {
			list: () => '/app/users/app-users',
			details: (userId: string) => `/app/users/app-users/${userId}`,
			detailsTabs: (userId: string, tab: EndUsersDetailTab) =>
				`/app/users/app-users/${userId}/${tab}`,
			activityDetails: (userId: string, activityId: string, type: string) =>
				`/app/users/app-users/${userId}/activity/${activityId}?type=${type}`,
		},
		tagsNfc: {
			base: () => '/app/users/tags-nfc',
			list: () => '/app/users/tags-nfc/overview',
			allWhitelist: () => '/app/users/tags-nfc/whitelist',
			tabs: (tab: TagsNfcTab) => `/app/users/tags-nfc/${tab}`,
			details: (tagId: string) => `/app/users/tags-nfc/${tagId}`,
			detailsTabs: (tagId: string, tab: EndUsersDetailTab) => `/app/users/tags-nfc/${tagId}/${tab}`,
			activityDetails: (tagId: string, activityId: string, type: string) =>
				`/app/users/tags-nfc/${tagId}/activity/${activityId}?type=${type}`,
			whitelist: {
				details: (tagId: string) => `/app/users/tags-nfc/whitelist/details/${tagId}`,
				detailsTabs: (tagId: string, tab: EndUsersDetailTab) =>
					`/app/users/tags-nfc/whitelist/details/${tagId}/${tab}`,
				activityDetails: (tagId: string, activityId: string, type: string) =>
					`/app/users/tags-nfc/whitelist/details/${tagId}/activity/${activityId}?type=${type}`,
			},
			oneWhitelist: (accessType: string, selector?: string | null) => {
				const base = '/app/users/tags-nfc/whitelist/details';
				if (accessType === 'global') return `${base}?global_access=true`;
				if (accessType === 'company') return `${base}?company=${selector}`;
				if (accessType === 'machine') return `${base}?machine=${selector}`;
				return `${base}?location=${selector}`;
			},
		},
		qrs: {
			base: () => '/app/users/qrs',
			list: () => '/app/users/qrs/overview',
			allWhitelist: () => '/app/users/qrs/whitelist',
			tabs: (tab: QrsTab) => `/app/users/qrs/${tab}`,
			details: (qrId: string) => `/app/users/qrs/${qrId}`,
			activityDetails: (qrId: string, activityId: string, type: string) =>
				`/app/users/qrs/${qrId}/activity/${activityId}?type=${type}`,
			whitelist: {
				details: (qrId: string) => `/app/users/qrs/whitelist/details/${qrId}`,
				activityDetails: (qrId: string, activityId: string, type: string) =>
					`/app/users/qrs/whitelist/details/${qrId}/activity/${activityId}?type=${type}`,
			},
			oneWhitelist: (accessType: string, selector?: string | null) => {
				const base = '/app/users/qrs/whitelist/details';
				if (accessType === 'global') return `${base}?global_access=true`;
				if (accessType === 'company') return `${base}?company=${selector}`;
				if (accessType === 'machine') return `${base}?machine=${selector}`;
				return `${base}?location=${selector}`;
			},
		},
		support: {
			list: () => '/app/users/user-support',
			details: (ticketId: string) => `/app/users/user-support/${ticketId}`,
			activityDetails: (activityId: string, type: string) =>
				`/app/users/user-support/activity/${activityId}?type=${type}`,
		},
	},

	benefits: {
		list: () => '/app/benefits',
		tabs: (tab: BenefitsTab) => `/app/benefits/${tab}`,
		promotions: {
			list: () => '/app/benefits/promotions',
			details: (promotionId: string) => `/app/benefits/promotions/${promotionId}`,
			create: () => '/app/benefits/promotions/create',
			detailsTabs: (promotionId: string, tab: BenefitsPromotionTabs) =>
				`/app/benefits/promotions/${promotionId}/${tab}`,
			activityDetails: (promotionId: string, transactionId: string) =>
				`/app/benefits/promotions/${promotionId}/activity/${transactionId}`,
		},
		packs: {
			list: () => '/app/benefits/packs',
			details: (packId: string) => `/app/benefits/packs/${packId}`,
			create: () => '/app/benefits/packs/create',
			detailsTabs: (packId: string, tab: BenefitsPacksTabs) =>
				`/app/benefits/packs/${packId}/${tab}`,
			activityDetails: (packId: string, transactionId: string) =>
				`/app/benefits/packs/${packId}/activity/${transactionId}`,
		},
	},

	settings: {
		main: () => '/app/settings',
		tabs: (tabId: SettingsTab) => `/app/settings/${tabId}`,
		create: (tabId: SettingsTab) => `/app/settings/${tabId}/create`,
		details: (tabId: SettingsTab, itemId: string) => `/app/settings/${tabId}/${itemId}`,
		editRole: (roleId: number) => `/app/settings/user-management/roles/${roleId}/edit`,
	},

	tools: {
		tabs: (tab: ToolsTab) => `/app/tools/${tab}`,
		reports: {
			base: () => '/app/tools/reports',
			templates: () => '/app/tools/reports/templates',
			templateDetails: (templateId: string) => `/app/tools/reports/templates/${templateId}`,
			create: () => '/app/tools/reports/create',
		},

		alerts: {
			base: () => '/app/tools/alerts',
			messages: () => '/app/tools/alerts/messages',
			configurations: () => '/app/tools/alerts/configurations',
			tabs: (tabId: AlertsTab) => `/app/tools/alerts/${tabId}`,
			create: () => '/app/tools/alerts/configurations/create',
			details: (configId: string) => `/app/tools/alerts/configurations/${configId}`,
			info: (configId: string) => `/app/tools/alerts/configurations/${configId}/info`,
			history: (configId: string) => `/app/tools/alerts/configurations/${configId}/history`,
		},
	},

	support: {
		base: () => '/app/support',
		tabs: (tabId: SupportTab) => `/app/support/${tabId}`,
	},

	finance: {
		base: () => '/app/finance',
		reconciliations: {
			list: () => '/app/finance/reconciliations',
			listTabs: (tab: ReconciliationTab) => `/app/finance/reconciliations/${tab}`,
			details: (reconciliationId: string, paymentProvider: string) =>
				`/app/finance/reconciliations/${reconciliationId}/overview?paymentProvider=${paymentProvider}`,
			detailTab: (reconciliationId: string, tab: ReconciliationTab, paymentProvider: string) =>
				`/app/finance/reconciliations/${reconciliationId}/${tab}?paymentProvider=${paymentProvider}`,
			transactionDetails: (
				reconciliationId: string,
				transactionId: string,
				type: TransactionType,
				providerReference?: string,
			) =>
				`/app/finance/reconciliations/${reconciliationId}/transaction/${transactionId}?type=${type}${
					providerReference ? `&providerReference=${encodeURIComponent(providerReference)}` : ''
				}`,
			settlementDetails: (reconciliationId: string, providerReference: string) =>
				`/app/finance/reconciliations/${reconciliationId}/settlement/${encodeURIComponent(providerReference)}`,
			posDetail: (reconciliationId: string, paymentProvider: string, machineId: number) =>
				`/app/finance/reconciliations/${reconciliationId}/point-of-sale/${machineId}?paymentProvider=${paymentProvider}`,
			locationDetail: (reconciliationId: string, paymentProvider: string, locationName: string) =>
				`/app/finance/reconciliations/${reconciliationId}/location/${encodeURIComponent(locationName)}?paymentProvider=${paymentProvider}`,
			delegationDetail: (
				reconciliationId: string,
				paymentProvider: string,
				delegationName: string,
			) =>
				`/app/finance/reconciliations/${reconciliationId}/delegation/${encodeURIComponent(delegationName)}?paymentProvider=${paymentProvider}`,
		},
	},

	marketplace: {
		base: () => '/app/marketplace',
		tabs: (tab: MarketplaceTab) => `/app/marketplace/${tab}`,
	},
} as const;
